// third-party
import { createSlice } from '@reduxjs/toolkit';

// project imports
import axios from 'utils/axios';
import { dispatch } from '../index';

// ----------------------------------------------------------------------

const initialState = {
    error: null,
    loading: false,
    // Legacy planning (fichier de ménage)
    planning: null,
    // Modern cleaning system
    schedules: [],
    housemaids: [],
    stats: null,
    housemaidSettings: {},
    unassignedCheckouts: [],
    cleaningReport: null
};

const slice = createSlice({
    name: 'cleaningControl',
    initialState,
    reducers: {
        // Loading state
        setLoading(state, action) {
            state.loading = action.payload;
        },

        // HAS ERROR
        hasError(state, action) {
            state.error = action.payload;
            state.loading = false;
        },

        // Legacy - GET Planning (fichier de ménage)
        getPlanningSuccess(state, action) {
            state.planning = action.payload;
            state.loading = false;
        },

        // Modern cleaning system
        getSchedulesSuccess(state, action) {
            state.schedules = action.payload;
            state.loading = false;
        },

        getHousemaidsSuccess(state, action) {
            state.housemaids = action.payload;
            state.loading = false;
        },

        getStatsSuccess(state, action) {
            state.stats = action.payload;
            state.loading = false;
        },

        getHousemaidSettingsSuccess(state, action) {
            state.housemaidSettings[action.payload.housemaidId] = action.payload.settings;
            state.loading = false;
        },

        updateScheduleSuccess(state, action) {
            const index = state.schedules.findIndex(s => s.id === action.payload.id);
            if (index !== -1) {
                // Mise à jour en place de l'objet schedule pour préserver les références
                state.schedules[index] = action.payload;
            }
            // Ne pas modifier loading ici car updateSchedule ne l'active plus
            // state.loading = false;
        },

        generatePlanningSuccess(state, action) {
            state.schedules = action.payload;
            state.loading = false;
        },

        freezePlanningSuccess(state) {
            state.schedules = state.schedules.map(s => ({ ...s, is_frozen: true }));
            state.loading = false;
        },

        unfreezePlanningSuccess(state) {
            state.schedules = state.schedules.map(s => ({ ...s, is_frozen: false }));
            state.loading = false;
        },

        getUnassignedCheckoutsSuccess(state, action) {
            state.unassignedCheckouts = action.payload;
            state.loading = false;
        },

        manualAssignSuccess(state, action) {
            // Add the new schedule to schedules
            state.schedules.push(action.payload);
            // Remove the assigned checkout from unassigned list
            state.unassignedCheckouts = state.unassignedCheckouts.filter(
                checkout => checkout.booking_id !== action.payload.booking_id
            );
            state.loading = false;
        },

        createManualCleaningSuccess(state, action) {
            // Add the new manual cleaning to schedules
            state.schedules.push(action.payload);
            state.loading = false;
        },

        getCleaningReportSuccess(state, action) {
            state.cleaningReport = action.payload;
            state.loading = false;
        }
    }
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

// Legacy function - GET Planning (fichier de ménage)
export function getPlanning(date) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get(`/api/booking/management/planning${date && date !== '' ? '/' + date : ''}`);
            dispatch(slice.actions.getPlanningSuccess(response.data.roomListBuckets));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

// Modern cleaning system actions
export function getSchedules(date) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get(`/api/cleaning/schedule/${date}`);
            dispatch(slice.actions.getSchedulesSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getHousemaids() {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get('/api/users/ROLE_HOUSEMAID?limit=100');
            dispatch(slice.actions.getHousemaidsSuccess(response.data.users));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getStats(date) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get(`/api/cleaning/stats/${date}`);
            dispatch(slice.actions.getStatsSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getHousemaidSettings(housemaidId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get(`/api/cleaning/settings/${housemaidId}`);
            dispatch(slice.actions.getHousemaidSettingsSuccess({
                housemaidId,
                settings: response.data.data
            }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateHousemaidSettings(housemaidId, settings) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.put(`/api/cleaning/settings/${housemaidId}`, settings);
            dispatch(slice.actions.getHousemaidSettingsSuccess({
                housemaidId,
                settings: response.data.data
            }));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateSchedule(scheduleId, updates) {
    return async () => {
        try {
            // Ne pas activer le loading pour éviter de détruire le calendrier et perdre le scroll
            // dispatch(slice.actions.setLoading(true));
            const response = await axios.put(`/api/cleaning/schedule/${scheduleId}`, updates);
            dispatch(slice.actions.updateScheduleSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function generatePlanning(date, forceRegenerate = false) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.post('/api/cleaning/schedule/generate', {
                date,
                forceRegenerate
            });
            dispatch(slice.actions.generatePlanningSuccess(response.data.data));

            // Recharger automatiquement les données après la génération
            dispatch(getSchedules(date));
            dispatch(getStats(date));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getUnassignedCheckouts(date) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get(`/api/cleaning/schedule/${date}/unassigned`);
            dispatch(slice.actions.getUnassignedCheckoutsSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function manualAssign(assignmentData) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.post('/api/cleaning/schedule/manual-assign', assignmentData);
            dispatch(slice.actions.manualAssignSuccess(response.data.data));

            // Refresh schedules and stats after assignment
            const date = assignmentData.scheduled_date;
            dispatch(getSchedules(date));
            dispatch(getStats(date));
            dispatch(getUnassignedCheckouts(date));

            return response.data;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function freezePlanning(date) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            await axios.post('/api/cleaning/schedule/freeze', { date });
            dispatch(slice.actions.freezePlanningSuccess());
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function unfreezePlanning(date) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            await axios.post('/api/cleaning/schedule/unfreeze', { date });
            dispatch(slice.actions.unfreezePlanningSuccess());
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function getHousemaidPlanning(housemaidId, date = null) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            let url = `/api/cleaning/housemaid/${housemaidId}/planning`;
            if (date) {
                url += `?date=${date}`;
            }
            const response = await axios.get(url);
            dispatch(slice.actions.getSchedulesSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function updateHousemaidScheduleStatus(housemaidId, scheduleId, status, cleaningReport = null) {
    return async () => {
        try {
            // Ne pas activer le loading pour éviter de bloquer l'interface
            // dispatch(slice.actions.setLoading(true));
            const payload = { status };
            if (cleaningReport) {
                payload.cleaning_report = cleaningReport;
            }
            const response = await axios.put(`/api/cleaning/housemaid/${housemaidId}/schedule/${scheduleId}/status`, payload);
            dispatch(slice.actions.updateScheduleSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
        }
    };
}

export function createManualCleaning(cleaningData) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.post('/api/cleaning/schedule/create-manual', cleaningData);
            dispatch(slice.actions.createManualCleaningSuccess(response.data.data));

            // Refresh schedules and stats after creation
            const date = cleaningData.scheduled_date;
            dispatch(getSchedules(date));
            dispatch(getStats(date));

            return response.data;
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function getCleaningReport(cleaningId) {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            const response = await axios.get(`/api/cleaning/report/${cleaningId}`);
            dispatch(slice.actions.getCleaningReportSuccess(response.data.data));
        } catch (error) {
            dispatch(slice.actions.hasError(error));
            throw error;
        }
    };
}

export function uploadCleaningPhoto(scheduleId, photoFile) {
    return async () => {
        try {
            // Create FormData for multipart upload
            const formData = new FormData();
            formData.append('photo', photoFile);

            // Upload photo with watermark generation
            const response = await axios.post(
                `/api/cleaning/schedule/${scheduleId}/photo`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            // Return the response data (contains original and watermarked photo info)
            return response.data.data;
        } catch (error) {
            console.error('Error uploading cleaning photo:', error);
            // Re-throw error so component can handle it
            throw error.response?.data || error;
        }
    };
}
